/**
 * Minimal, dependency-free ZIP writer (STORE method, no compression).
 *
 * - Produces a standard ZIP archive that opens in Windows Explorer, macOS Finder, 7-Zip, etc.
 * - File names are written as UTF-8 (general purpose bit 11), so non-ASCII names are preserved.
 * - Documents such as .docx/.xlsx/.pptx/.pdf are already compressed, so STORE keeps the
 *   archive fast to build without meaningfully increasing its size.
 * - Limits: no ZIP64, so each file and the whole archive must stay below 4 GB, and there
 *   can be at most 65,535 entries.
 */

export type ZipData = Blob | Uint8Array | ArrayBuffer | string;

export interface ZipEntry {
    /** Path inside the archive, using `/` as the separator, e.g. `01 Process flows/As-Is/file.pdf` */
    path: string;
    data: ZipData;
    /** Last-modified time stored in the archive (defaults to now) */
    date?: Date;
}

const CRC_TABLE: Uint32Array = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

export function crc32(bytes: Uint8Array): number {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    }
    return (crc ^ 0xFFFFFFFF) >>> 0;
}

async function toBytes(data: ZipData): Promise<Uint8Array> {
    if (typeof data === 'string') { return new TextEncoder().encode(data); }
    if (data instanceof Uint8Array) { return data; }
    if (data instanceof ArrayBuffer) { return new Uint8Array(data); }
    return new Uint8Array(await data.arrayBuffer());
}

/** MS-DOS date/time encoding used by the ZIP format (2-second resolution, years 1980–2107). */
function dosDateTime(d: Date): { time: number; date: number } {
    const year = Math.min(Math.max(d.getFullYear(), 1980), 2107);
    return {
        time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
        date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()
    };
}

/** Builds a ZIP archive from the given entries. */
export async function createZip(entries: ZipEntry[]): Promise<Blob> {
    if (entries.length > 0xFFFF) { throw new Error('Too many files for a ZIP archive (max 65,535).'); }

    const encoder = new TextEncoder();
    const prepared = await Promise.all(entries.map(async e => {
        const name = encoder.encode(e.path.replace(/\\/g, '/').replace(/^\/+/, ''));
        const data = await toBytes(e.data);
        return { name, data, crc: crc32(data), ...dosDateTime(e.date ?? new Date()) };
    }));

    const localSize = prepared.reduce((a, p) => a + 30 + p.name.length + p.data.length, 0);
    const centralSize = prepared.reduce((a, p) => a + 46 + p.name.length, 0);
    const total = localSize + centralSize + 22;
    if (total > 0xFFFFFFFF) { throw new Error('The pack is too large for a ZIP archive (max 4 GB).'); }

    const out = new Uint8Array(total);
    const view = new DataView(out.buffer);
    const FLAG_UTF8 = 0x0800;
    let pos = 0;
    const offsets: number[] = [];

    // local file headers + data
    for (const p of prepared) {
        offsets.push(pos);
        view.setUint32(pos, 0x04034B50, true);
        view.setUint16(pos + 4, 20, true);              // version needed
        view.setUint16(pos + 6, FLAG_UTF8, true);
        view.setUint16(pos + 8, 0, true);               // method: store
        view.setUint16(pos + 10, p.time, true);
        view.setUint16(pos + 12, p.date, true);
        view.setUint32(pos + 14, p.crc, true);
        view.setUint32(pos + 18, p.data.length, true);  // compressed size
        view.setUint32(pos + 22, p.data.length, true);  // uncompressed size
        view.setUint16(pos + 26, p.name.length, true);
        view.setUint16(pos + 28, 0, true);              // extra length
        out.set(p.name, pos + 30);
        out.set(p.data, pos + 30 + p.name.length);
        pos += 30 + p.name.length + p.data.length;
    }

    // central directory
    const cdStart = pos;
    prepared.forEach((p, i) => {
        view.setUint32(pos, 0x02014B50, true);
        view.setUint16(pos + 4, 20, true);              // version made by
        view.setUint16(pos + 6, 20, true);              // version needed
        view.setUint16(pos + 8, FLAG_UTF8, true);
        view.setUint16(pos + 10, 0, true);
        view.setUint16(pos + 12, p.time, true);
        view.setUint16(pos + 14, p.date, true);
        view.setUint32(pos + 16, p.crc, true);
        view.setUint32(pos + 20, p.data.length, true);
        view.setUint32(pos + 24, p.data.length, true);
        view.setUint16(pos + 28, p.name.length, true);
        // extra len, comment len, disk no, internal attrs, external attrs = 0
        view.setUint32(pos + 42, offsets[i], true);
        out.set(p.name, pos + 46);
        pos += 46 + p.name.length;
    });

    // end of central directory
    view.setUint32(pos, 0x06054B50, true);
    view.setUint16(pos + 8, prepared.length, true);
    view.setUint16(pos + 10, prepared.length, true);
    view.setUint32(pos + 12, pos - cdStart, true);
    view.setUint32(pos + 16, cdStart, true);

    return new Blob([out.buffer as ArrayBuffer], { type: 'application/zip' });
}

/** Replaces characters that are not allowed in Windows/macOS file or folder names. */
export function safeFileName(name: string, fallback: string = 'file'): string {
    const cleaned = name
        .replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[. ]+$/, '');
    return (cleaned || fallback).slice(0, 180);
}
